/**
 * @typedef {import('firebase/firestore').Firestore} Firestore
 * @typedef {import('firebase/firestore').Timestamp} FirestoreTimestamp
 */

/**
 * @param {{
 *   db: Firestore,
 *   eventsCollection: string,
 *   checkFirestore: () => boolean,
 *   getPublishedEvents: () => Promise<Array<Record<string, unknown>>>,
 *   getEventCalendarDay: (event: Record<string, unknown>) => Date | null,
 *   firestore: {
 *     collection: typeof import('firebase/firestore').collection,
 *     query: typeof import('firebase/firestore').query,
 *     where: typeof import('firebase/firestore').where,
 *     orderBy: typeof import('firebase/firestore').orderBy,
 *     limit: typeof import('firebase/firestore').limit,
 *     startAfter: typeof import('firebase/firestore').startAfter,
 *     getDocs: typeof import('firebase/firestore').getDocs,
 *     Timestamp: { fromDate: (date: Date) => FirestoreTimestamp },
 *   },
 * }} deps
 * @returns {(max?: number) => Promise<Array<Record<string, unknown>>>}
 */
export function createGetUpcomingPublishedEvents(deps) {
  const getPage = createGetUpcomingPublishedEventsPage(deps);

  return async (max = 8) => {
    const page = await getPage({ pageSize: max });
    return page.events;
  };
}

/**
 * Paginated upcoming published events (cursor = Firestore doc snapshot, or numeric offset in fallback mode).
 *
 * @param {Parameters<typeof createGetUpcomingPublishedEvents>[0]} deps
 * @returns {(opts?: { pageSize?: number, cursor?: import('firebase/firestore').QueryDocumentSnapshot | number | null }) => Promise<{ events: Array<Record<string, unknown>>, nextCursor: import('firebase/firestore').QueryDocumentSnapshot | number | null, hasMore: boolean }>}
 */
export function createGetUpcomingPublishedEventsPage(deps) {
  const {
    db,
    eventsCollection,
    checkFirestore,
    getPublishedEvents,
    getEventCalendarDay,
    firestore: { collection, query, where, orderBy, limit, startAfter, getDocs, Timestamp },
  } = deps;

  // Cache full-collection fallback so chain-paging does not re-download v2-events.
  let fallbackUpcomingPromise = null;

  const getFallbackUpcoming = () => {
    if (!fallbackUpcomingPromise) {
      fallbackUpcomingPromise = (async () => {
        const published = await getPublishedEvents();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return published
          .filter((event) => {
            const day = getEventCalendarDay(event);
            return day && day >= startOfToday;
          })
          .sort((a, b) => {
            const dayA = getEventCalendarDay(a);
            const dayB = getEventCalendarDay(b);
            return dayA.getTime() - dayB.getTime();
          });
      })().catch((err) => {
        fallbackUpcomingPromise = null;
        throw err;
      });
    }
    return fallbackUpcomingPromise;
  };

  const filterUpcomingPublished = async (pageSize, cursor) => {
    const upcoming = await getFallbackUpcoming();
    const offset = typeof cursor === 'number' ? cursor : 0;
    const slice = upcoming.slice(offset, offset + pageSize + 1);
    const hasMore = slice.length > pageSize;
    const events = slice.slice(0, pageSize);
    const nextCursor = hasMore ? offset + pageSize : null;
    return { events, nextCursor, hasMore };
  };

  return async ({ pageSize = 12, cursor = null } = {}) => {
    if (!checkFirestore()) {
      return { events: [], nextCursor: null, hasMore: false };
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    try {
      const constraints = [
        where('status', '==', 'published'),
        where('eventStartAt', '>=', Timestamp.fromDate(startOfToday)),
        orderBy('eventStartAt', 'asc'),
      ];
      if (cursor && typeof cursor !== 'number') {
        constraints.push(startAfter(cursor));
      }
      constraints.push(limit(pageSize + 1));

      const snapshot = await getDocs(query(collection(db, eventsCollection), ...constraints));
      const docs = snapshot.docs;
      const hasMore = docs.length > pageSize;
      const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
      const events = pageDocs.map((d) => ({ id: d.id, ...d.data() }));
      const nextCursor = hasMore ? pageDocs[pageDocs.length - 1] : null;
      return { events, nextCursor, hasMore };
    } catch (error) {
      const needsIndex =
        error?.code === 'failed-precondition' ||
        String(error?.message || '').includes('requires an index');
      if (!needsIndex) {
        console.error('Error fetching upcoming published events page:', error);
        return { events: [], nextCursor: null, hasMore: false };
      }
      // Composite index for status + eventStartAt lives in firestore.indexes.json.
      // Fallback still pages in memory but downloads the collection at most once.
      console.warn(
        'Firestore index not ready for upcoming published events; falling back to a single client-side collection load. Deploy firestore.indexes.json if this persists.'
      );
      return filterUpcomingPublished(pageSize, cursor);
    }
  };
}

/**
 * Upcoming published homepage-featured picks (editorialPick array-contains "saveTheDate").
 * Save the date featured block; "Best of season" picks cascade into `saveTheDate` too,
 * so every season pick also lands here (see eventEditorialPick.js).
 *
 * @param {{
 *   db: Firestore,
 *   eventsCollection: string,
 *   checkFirestore: () => boolean,
 *   getPublishedEvents: () => Promise<Array<Record<string, unknown>>>,
 *   getEventCalendarDay: (event: Record<string, unknown>) => Date | null,
 *   isHomepageFeaturedEditorialPick: (value: unknown, event?: Record<string, unknown>|null) => boolean,
 *   sortFeaturedEventsByDate: (events: Array<Record<string, unknown>>) => Array<Record<string, unknown>>,
 *   firestore: {
 *     collection: typeof import('firebase/firestore').collection,
 *     query: typeof import('firebase/firestore').query,
 *     where: typeof import('firebase/firestore').where,
 *     orderBy: typeof import('firebase/firestore').orderBy,
 *     limit: typeof import('firebase/firestore').limit,
 *     getDocs: typeof import('firebase/firestore').getDocs,
 *     Timestamp: { fromDate: (date: Date) => FirestoreTimestamp },
 *   },
 * }} deps
 * @returns {(opts?: { pageSize?: number }) => Promise<Array<Record<string, unknown>>>}
 */
export function createGetUpcomingFeaturedPublishedEvents(deps) {
  const {
    db,
    eventsCollection,
    checkFirestore,
    getPublishedEvents,
    getEventCalendarDay,
    isHomepageFeaturedEditorialPick,
    sortFeaturedEventsByDate,
    firestore: { collection, query, where, orderBy, limit, getDocs, Timestamp },
  } = deps;

  const filterFeaturedFallback = async (pageSize) => {
    const published = await getPublishedEvents();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const featured = published.filter((event) => {
      const day = getEventCalendarDay(event);
      if (!day || day < startOfToday) return false;
      return isHomepageFeaturedEditorialPick(event.editorialPick, event);
    });
    return sortFeaturedEventsByDate(featured).slice(0, pageSize);
  };

  return async ({ pageSize = 40 } = {}) => {
    if (!checkFirestore()) return [];

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    try {
      const q = query(
        collection(db, eventsCollection),
        where('status', '==', 'published'),
        where('editorialPick', 'array-contains', 'saveTheDate'),
        where('eventStartAt', '>=', Timestamp.fromDate(startOfToday)),
        orderBy('eventStartAt', 'asc'),
        orderBy('editorialPickRank', 'desc'),
        limit(pageSize)
      );
      const snapshot = await getDocs(q);
      const events = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((event) => isHomepageFeaturedEditorialPick(event.editorialPick, event));
      return sortFeaturedEventsByDate(events);
    } catch (error) {
      const needsIndex =
        error?.code === 'failed-precondition' ||
        String(error?.message || '').includes('requires an index');
      if (!needsIndex) {
        console.error('Error fetching upcoming featured published events:', error);
        return [];
      }
      console.warn(
        'Firestore index not ready for featured published events; falling back to client-side filter. Deploy firestore.indexes.json if this persists.'
      );
      return filterFeaturedFallback(pageSize);
    }
  };
}

const EMPTY_EVENT_AGGREGATE = {
  upcomingCount: 0,
  byDay: {},
  byCategory: {},
  byLocation: {},
  tags: [],
  updatedAt: null,
};

function aggregateUpdatedAtMillis(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === 'function') {
    const ms = value.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (value && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

/**
 * Public homepage chrome counts (`eventAggregates/upcomingPublished`).
 *
 * @param {{
 *   db: Firestore,
 *   checkFirestore: () => boolean,
 *   aggregateCollection?: string,
 *   aggregateDocId?: string,
 *   firestore: {
 *     doc: typeof import('firebase/firestore').doc,
 *     getDoc: typeof import('firebase/firestore').getDoc,
 *   },
 * }} deps
 * @returns {() => Promise<{ upcomingCount: number, byDay: Record<string, number>, byCategory: Record<string, number>, byLocation: Record<string, number>, tags: string[], updatedAt: number|null }>}
 */
export function createGetEventAggregate(deps) {
  const {
    db,
    checkFirestore,
    aggregateCollection = 'eventAggregates',
    aggregateDocId = 'upcomingPublished',
    firestore: { doc, getDoc },
  } = deps;

  return async () => {
    if (!checkFirestore()) return { ...EMPTY_EVENT_AGGREGATE };
    try {
      const snap = await getDoc(doc(db, aggregateCollection, aggregateDocId));
      if (!snap.exists()) return { ...EMPTY_EVENT_AGGREGATE };
      const data = snap.data() || {};
      return {
        upcomingCount: typeof data.upcomingCount === 'number' ? data.upcomingCount : 0,
        byDay: data.byDay && typeof data.byDay === 'object' ? data.byDay : {},
        byCategory: data.byCategory && typeof data.byCategory === 'object' ? data.byCategory : {},
        byLocation: data.byLocation && typeof data.byLocation === 'object' ? data.byLocation : {},
        tags: Array.isArray(data.tags) ? data.tags : [],
        updatedAt: aggregateUpdatedAtMillis(data.updatedAt),
      };
    } catch (error) {
      console.error('Error fetching event aggregate:', error);
      throw error;
    }
  };
}

/**
 * Published events whose dateStr calendar day is in [start, end).
 * Queries eventStartAt padded by one day, then filters by dateStr.
 */
export function createGetUpcomingPublishedEventsInRange(deps) {
  const {
    db,
    eventsCollection,
    checkFirestore,
    getPublishedEvents,
    getEventCalendarDay,
    firestore: { collection, query, where, orderBy, getDocs, Timestamp },
  } = deps;

  const inCalendarWindow = (event, start, end) => {
    const day = getEventCalendarDay(event);
    return Boolean(day && day >= start && day < end);
  };

  const sortByCalendarDay = (events) =>
    events.slice().sort((a, b) => {
      const dayA = getEventCalendarDay(a);
      const dayB = getEventCalendarDay(b);
      return dayA.getTime() - dayB.getTime();
    });

  const filterRangeFallback = async (start, end) => {
    const published = await getPublishedEvents();
    return sortByCalendarDay(published.filter((event) => inCalendarWindow(event, start, end)));
  };

  return async ({ start, end } = {}) => {
    if (!checkFirestore() || !(start instanceof Date) || !(end instanceof Date)) {
      return [];
    }

    const queryStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1);
    const queryEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);

    try {
      const q = query(
        collection(db, eventsCollection),
        where('status', '==', 'published'),
        where('eventStartAt', '>=', Timestamp.fromDate(queryStart)),
        where('eventStartAt', '<', Timestamp.fromDate(queryEnd)),
        orderBy('eventStartAt', 'asc')
      );
      const snapshot = await getDocs(q);
      return sortByCalendarDay(
        snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((event) => inCalendarWindow(event, start, end))
      );
    } catch (error) {
      const needsIndex =
        error?.code === 'failed-precondition' ||
        String(error?.message || '').includes('requires an index');
      if (!needsIndex) {
        console.error('Error fetching upcoming published events in range:', error);
        return [];
      }
      console.warn(
        'Firestore index not ready for upcoming published events range; falling back to a single client-side collection load. Deploy firestore.indexes.json if this persists.'
      );
      return filterRangeFallback(start, end);
    }
  };
}

/**
 * Get upcoming events (from today forward) regardless of status.
 * This is useful for admin pages where we want to see all statuses but only future events.
 * 
 * @param {{
 *   db: Firestore,
 *   eventsCollection: string,
 *   checkFirestore: () => boolean,
 *   getAllEvents: () => Promise<Array<Record<string, unknown>>>,
 *   getEventCalendarDay: (event: Record<string, unknown>) => Date | null,
 *   firestore: {
 *     collection: typeof import('firebase/firestore').collection,
 *     query: typeof import('firebase/firestore').query,
 *     where: typeof import('firebase/firestore').where,
 *     orderBy: typeof import('firebase/firestore').orderBy,
 *     getDocs: typeof import('firebase/firestore').getDocs,
 *     Timestamp: { fromDate: (date: Date) => FirestoreTimestamp },
 *   },
 * }} deps
 * @returns {() => Promise<Array<Record<string, unknown>>>}
 */
export function createGetUpcomingEvents(deps) {
  const {
    db,
    eventsCollection,
    checkFirestore,
    getAllEvents,
    getEventCalendarDay,
    firestore: { collection, query, where, orderBy, getDocs, Timestamp },
  } = deps;

  return async () => {
    if (!checkFirestore()) return [];

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    try {
      const q = query(
        collection(db, eventsCollection),
        where('eventStartAt', '>=', Timestamp.fromDate(startOfToday)),
        orderBy('eventStartAt', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      const needsIndex =
        error?.code === 'failed-precondition' ||
        String(error?.message || '').includes('requires an index');
      
      if (!needsIndex) {
        console.error('Error fetching upcoming events:', error);
        return [];
      }
      
      // Fallback: fetch all events and filter client-side
      console.warn('Firestore index not ready; falling back to client-side filtering for upcoming events');
      const allEvents = await getAllEvents();
      return allEvents
        .filter((event) => {
          const day = getEventCalendarDay(event);
          return day && day >= startOfToday;
        })
        .sort((a, b) => {
          const dayA = getEventCalendarDay(a);
          const dayB = getEventCalendarDay(b);
          if (!dayA || !dayB) return 0;
          return dayA.getTime() - dayB.getTime();
        });
    }
  };
}

/**
 * Upcoming published events at a MapTODO place (`locationId` wire field).
 *
 * @param {Parameters<typeof createGetUpcomingPublishedEvents>[0]} deps
 * @returns {(placeId: string, opts?: { limit?: number }) => Promise<Array<Record<string, unknown>>>}
 */
export function createGetUpcomingPublishedEventsAtLocation(deps) {
  const {
    db,
    eventsCollection,
    checkFirestore,
    getPublishedEvents,
    getEventCalendarDay,
    firestore: { collection, query, where, orderBy, limit, getDocs, Timestamp },
  } = deps;

  const matchesPlace = (event, placeId) =>
    String(event?.locationId || '').trim() === placeId;

  const filterFallback = async (placeId, pageSize) => {
    const published = await getPublishedEvents();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return published
      .filter((event) => {
        if (!matchesPlace(event, placeId)) return false;
        const day = getEventCalendarDay(event);
        return day && day >= startOfToday;
      })
      .sort((a, b) => {
        const dayA = getEventCalendarDay(a);
        const dayB = getEventCalendarDay(b);
        return dayA.getTime() - dayB.getTime();
      })
      .slice(0, pageSize);
  };

  return async (placeId, { limit: pageSize = 5 } = {}) => {
    const id = String(placeId || '').trim();
    if (!checkFirestore() || !id) return [];

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    try {
      const snapshot = await getDocs(
        query(
          collection(db, eventsCollection),
          where('status', '==', 'published'),
          where('locationId', '==', id),
          where('eventStartAt', '>=', Timestamp.fromDate(startOfToday)),
          orderBy('eventStartAt', 'asc'),
          limit(pageSize)
        )
      );
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      const needsIndex =
        error?.code === 'failed-precondition' ||
        String(error?.message || '').includes('requires an index');
      if (!needsIndex) {
        console.error('Error fetching upcoming events at location:', error);
        return [];
      }
      console.warn(
        'Firestore index not ready for events at location; falling back to a single client-side collection load. Deploy firestore.indexes.json if this persists.'
      );
      return filterFallback(id, pageSize);
    }
  };
}
